import { Link } from "react-router-dom";
import { mainSiteUrl } from "@/mocks/categories";

export default function ContentSection() {
  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-8">
            What Is Vigorbuy Spreadsheet?
          </h2>

          <div className="space-y-5 text-sm md:text-base text-foreground-700 leading-relaxed">
            <p>
              <strong className="text-foreground-900">Vigorbuy Spreadsheet</strong> is an innovative product discovery platform designed to transform the way shoppers find and explore products online. Unlike traditional browsing methods where you spend hours scrolling through endless listings, Vigorbuy Spreadsheet organizes high-quality products into meticulously curated, category-based databases that make product discovery fast, intuitive, and genuinely enjoyable.
            </p>
            <p>
              At its core, the platform operates as a comprehensive product catalog spanning fourteen distinct categories — from <strong className="text-foreground-900">Shoes</strong> and <strong className="text-foreground-900">Hoodies &amp; Sweaters</strong> to <strong className="text-foreground-900">Bags</strong>, <strong className="text-foreground-900">Perfumes</strong>, and <strong className="text-foreground-900">Accessories</strong>. Each category contains carefully selected items with direct shopping links, allowing users to browse collections and instantly navigate to product pages on trusted shopping platforms to complete their purchases.
            </p>
            <p>
              What sets Vigorbuy Spreadsheet apart is its commitment to organization and accessibility. Products are arranged in a logical, easy-to-navigate structure that eliminates the friction of traditional online shopping. Whether you are a seasoned bargain hunter searching for hidden gems or a casual shopper looking to refresh your wardrobe, the spreadsheet format provides a bird's-eye view of available products across multiple categories — all in one centralized location.
            </p>
            <p>
              Curious how Vigorbuy compares to other spreadsheet platforms? Check out our <Link to="/kakobuy-vs-vigorbuy" className="text-primary-600 hover:text-primary-700 font-medium underline">Kakobuy vs Vigorbuy detailed comparison</Link> for an in-depth feature-by-feature analysis.
            </p>
          </div>

          <div className="mt-10 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-background-300/60">
                  <th className="text-left py-3 px-4 font-semibold text-foreground-900">Feature</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-600">Normal Searching</th>
                  <th className="text-left py-3 px-4 font-semibold text-primary-700 bg-primary-50/50 rounded-t-lg">Vigorbuy Spreadsheet</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                <tr>
                  <td className="py-3 px-4 font-medium text-foreground-800">Product Discovery</td>
                  <td className="py-3 px-4 text-foreground-600">Slow &amp; Manual</td>
                  <td className="py-3 px-4 text-primary-700 bg-primary-50/30 font-medium">Fast &amp; Organized</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-foreground-800">Organization</td>
                  <td className="py-3 px-4 text-foreground-600">Scattered</td>
                  <td className="py-3 px-4 text-primary-700 bg-primary-50/30 font-medium">Fully Categorized</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-foreground-800">Research Time</td>
                  <td className="py-3 px-4 text-foreground-600">Hours</td>
                  <td className="py-3 px-4 text-primary-700 bg-primary-50/30 font-medium">Minutes</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-foreground-800">Beginner Friendly</td>
                  <td className="py-3 px-4 text-foreground-600">Medium</td>
                  <td className="py-3 px-4 text-primary-700 bg-primary-50/30 font-medium">Very Easy</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-foreground-800">Product Comparison</td>
                  <td className="py-3 px-4 text-foreground-600">Difficult</td>
                  <td className="py-3 px-4 text-primary-700 bg-primary-50/30 font-medium">Side-by-Side Easy</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-10 p-6 md:p-8 bg-primary-50/50 rounded-2xl border border-primary-200/50">
            <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-3">
              Why Shoppers Choose Vigorbuy Spreadsheet
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
              {[
                { title: "Product Discovery", desc: "Instantly find curated products across 14+ categories without endless scrolling through unorganized listings." },
                { title: "QC Information", desc: "Access real quality-check photos and product details so you know exactly what you are purchasing before clicking buy." },
                { title: "Category Navigation", desc: "Navigate seamlessly between Shoes, Jackets, Bags, Accessories, and more with one-click category switching." },
                { title: "Shopping Research", desc: "Compare multiple products side by side, evaluate prices, and make informed purchasing decisions effortlessly." },
                { title: "Time Savings", desc: "Reduce hours of manual searching to just minutes of organized browsing through the spreadsheet database." },
                { title: "Always Updated", desc: "Product listings are refreshed regularly to ensure you always see the latest, in-stock items across every category." },
              ].map((item) => (
                <div key={item.title} className="flex gap-3">
                  <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-500"></i>
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{item.title}</p>
                    <p className="text-xs text-foreground-600 mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/article/vigorbuy-spreadsheet-complete-guide"
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 border border-background-300/60 rounded-full text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-all duration-200"
            >
              Read the Complete Guide
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/shopping-guide"
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 border border-background-300/60 rounded-full text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-all duration-200"
            >
              View Shopping Guide
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}